import styled, { css } from 'styled-components';

import {
  activeInput,
  activeLayer180Deg,
  flexBoxCenter,
  justifyContentSpaceBetween,
  layerA,
  layerA180Deg,
  layerC,
  readyTop180Deg,
} from '../styles/commonStyles';

const ICONS = {
  instantHeat: '/images/logo-instantHeat.svg',
  fanOnly: '/images/tgs-fanOnly.svg',
  snowSensor: '/images/logo-snowSensor.svg',
  optionalConstantTemp: '/images/logo-constantTemp.svg',
  heatingSchedule: '/images/logo-schedule.svg',
  windFactor: '/images/logo-windFactor.svg',
};

// fanOnly is a plain on/off flag; the other programs are
// `{ isActivated, isReady, … }`.
const programState = (device, program) =>
  program === 'fanOnly' ? { isActivated: device.fanOnly } : device[program];

/**
 * The mobile control box's row of program buttons. A program's button opens
 * its panel, or closes it when open; the logo closes whatever is open.
 * `onPick(program)` / `onPick('unselect')`.
 */
const MobileProgramPicker = ({ programs, device, onPick }) => {
  const selected = device.mobileSelectedProgram;
  const anyOpen = Object.values(selected).includes(true);

  return (
    <SectionMobileSwitches isExpanded={anyOpen}>
      <MobileSwitchesInner>
        <SectionLogo>
          <Button noButton={!anyOpen} onClick={() => onPick('unselect')}>
            <ButtonHole>
              <ButtonTop>
                <IconImg src='/images/mc-title.svg' isMc={true} />
              </ButtonTop>
            </ButtonHole>
          </Button>
        </SectionLogo>

        <SectionController>
          {programs.map((program) => {
            const { isActivated, isReady } = programState(device, program);
            return (
              <SectionLogo key={program}>
                <Button
                  isActivated={isActivated}
                  isReady={isReady}
                  isSelected={selected[program]}
                  onClick={() => onPick(program)}
                >
                  <ButtonHole isActivated={isActivated}>
                    <IconImg src={ICONS[program]} />
                  </ButtonHole>
                </Button>
              </SectionLogo>
            );
          })}
        </SectionController>
      </MobileSwitchesInner>
    </SectionMobileSwitches>
  );
};

export default MobileProgramPicker;

const SectionMobileSwitches = styled.section`
  width: 303px;
  height: 60px;
  border-radius: 33px;
  ${layerA};
  ${flexBoxCenter};
  ${(p) =>
    p.isExpanded &&
    css`
      margin-bottom: 4px;
    `}
`;

const MobileSwitchesInner = styled.div`
  width: 300px;
  height: 58px;
  border-radius: 31px;
  ${layerA180Deg};
  ${justifyContentSpaceBetween};
  padding: 0 6px;
`;

const SectionLogo = styled.section`
  width: 44px;
  height: 44px;
  ${layerC}
  border-radius: 50%;
  ${flexBoxCenter}
`;

const Button = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  ${layerA180Deg};
  ${flexBoxCenter};

  ${(p) =>
    p.noButton &&
    css`
      cursor: default;
    `}

  ${(p) =>
    p.isSelected &&
    css`
      border: 1px solid #95ff45;
    `}

  ${(p) =>
    p.isReady &&
    css`
      ${readyTop180Deg};
    `};
  ${(p) =>
    p.isActivated &&
    css`
      ${activeLayer180Deg};
    `};
`;

const ButtonHole = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  ${layerA};
  ${flexBoxCenter};
  ${(p) =>
    p.isActivated &&
    css`
      ${activeInput};
    `};
`;

const ButtonTop = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  ${layerA180Deg};
  ${flexBoxCenter};
`;

const IconImg = styled.img`
  ${(p) =>
    p.isMc ||
    css`
      height: 100%;
    `}
`;

const SectionController = styled.section`
  width: 238px;
  height: 48px;
  border-radius: 26px;

  ${layerA};
  ${justifyContentSpaceBetween};
  padding: 0 2px;
`;
